import React from "react";
import { useTranslation } from "react-i18next";
import { ReportFormData } from "../../types/ReportFormData";
import { ScreenGroup } from "../../types/ScreenGroup";
import { Monitor } from "../../types/Screen";
import { getAvailableScreens } from "../../utils/screenGroupHelpers";
import SearchableMultiSelect from "./SearchableMultiSelect";
import Input from "../ui/Input";
import Button from "../ui/Button";

interface GroupScreensSectionProps {
	formData: ReportFormData;
	onChange: (data: Partial<ReportFormData>) => void;
	screenOptions: Monitor[];
	errors?: Record<string, string>;
}

const GroupScreensSection: React.FC<GroupScreensSectionProps> = ({
	formData,
	onChange,
	screenOptions,
	errors,
}) => {
	const { t } = useTranslation();

	const handleAddGroup = () => {
		const newEntry: ScreenGroup = { name: "", screen_ids: [] };
		onChange({ screen_groups: [...formData.screen_groups, newEntry] });
	};

	const handleRemoveGroup = (index: number) => {
		onChange({
			screen_groups: formData.screen_groups.filter((_, i) => i !== index),
		});
	};

	const handleNameChange = (index: number, name: string) => {
		const updated = [...formData.screen_groups];
		updated[index] = { ...updated[index], name };
		onChange({ screen_groups: updated });
	};

	const handleScreensChange = (index: number, monitors: Monitor[]) => {
		const updated = [...formData.screen_groups];
		updated[index] = {
			...updated[index],
			screen_ids: monitors.map((m) => m.Monitor_ID),
		};
		onChange({ screen_groups: updated });
	};

	const getAvailableForGroup = (currentIndex: number): Monitor[] => {
		const currentGroup = formData.screen_groups[currentIndex];
		const otherGroups = formData.screen_groups.filter(
			(_, i) => i !== currentIndex,
		);
		return getAvailableScreens(
			screenOptions,
			formData.excluded_screen_IDs,
			formData.screen_renames,
			otherGroups,
			currentGroup?.screen_ids ?? [],
		);
	};

	const getSelectedMonitors = (group: ScreenGroup): Monitor[] => {
		return group.screen_ids
			.map((id) => screenOptions.find((s) => s.Monitor_ID === id))
			.filter((s): s is Monitor => s !== undefined);
	};

	const hasAvailableScreens =
		getAvailableScreens(
			screenOptions,
			formData.excluded_screen_IDs,
			formData.screen_renames,
			formData.screen_groups,
		).length > 0;

	const noopFetch = async (): Promise<Monitor[]> => [];

	return (
		<div className="space-y-3">
			<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
				<label className="block text-gray-700 text-sm font-bold">
					{t("form.group.title")}
				</label>
				<Button
					type="button"
					size="sm"
					variant="secondary"
					onClick={handleAddGroup}
					disabled={!hasAvailableScreens}
					fullWidth={false}
					className="w-full sm:w-auto"
				>
					{t("form.group.addGroup")}
				</Button>
			</div>

			{formData.screen_groups.length === 0 ? (
				<p className="text-sm text-gray-500">{t("form.group.empty")}</p>
			) : (
				<div className="space-y-4">
					{formData.screen_groups.map((group, index) => {
						const available = getAvailableForGroup(index);
						const nameErrorKey = `screen_groups_name_${index}`;
						const sizeErrorKey = `screen_groups_size_${index}`;

						return (
							<div
								key={index}
								className="p-5 border border-gray-200 rounded-xl bg-gradient-to-br from-gray-50 to-primary-50/30 shadow-sm hover:shadow-md transition-shadow"
							>
								<div className="flex items-center justify-between mb-4">
									<h3 className="font-semibold text-gray-800">
										{t("form.group.groupTitle", { number: index + 1 })}
									</h3>
									<Button
										type="button"
										size="sm"
										variant="danger"
										onClick={() => handleRemoveGroup(index)}
									>
										{t("common.remove")}
									</Button>
								</div>

								<div className="space-y-4">
									<Input
										label={t("form.group.groupName")}
										value={group.name}
										onChange={(e) => handleNameChange(index, e.target.value)}
										placeholder={t("form.group.groupNamePlaceholder")}
										error={errors?.[nameErrorKey]}
									/>

									<SearchableMultiSelect<Monitor>
										label={t("form.group.screens")}
										selectedItems={getSelectedMonitors(group)}
										onChange={(items) => handleScreensChange(index, items)}
										fetchOptions={noopFetch}
										preloadedOptions={available}
										placeholder={t("form.group.screensPlaceholder")}
									/>

									{group.screen_ids.length > 0 &&
										group.screen_ids.length < 2 && (
											<p className="text-sm text-amber-600 flex items-center gap-1">
												<svg
													className="w-4 h-4 flex-shrink-0"
													fill="currentColor"
													viewBox="0 0 20 20"
												>
													<path
														fillRule="evenodd"
														d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
														clipRule="evenodd"
													/>
												</svg>
												{t("form.group.minScreensWarning")}
											</p>
										)}

									{errors?.[sizeErrorKey] && (
										<p className="text-sm text-red-600">
											{errors[sizeErrorKey]}
										</p>
									)}
								</div>
							</div>
						);
					})}
				</div>
			)}

			{errors?.screen_groups && (
				<p className="text-sm text-red-600">{errors.screen_groups}</p>
			)}
		</div>
	);
};

export default GroupScreensSection;
